import PropTypes from 'prop-types';
import React, { Component } from 'react';
import ContentCaption from './content_caption';
import withEscapeHatch from '../hocs/withEscapeHatch';
import { findAspectRatioFromResource } from '../../utils/widgetInspector';
import { sendAnalyticsEvent } from '../../utils/analytics';
import {
  extractVideoUrlFromEmbedCode,
  initializeFacebookPlayer
} from '../../utils/video/facebook';
import { FB_READY_HANDLER_TIMEOUT } from '../../constants';
import {some, memoize} from 'lodash';

export class FacebookVideoSection extends Component {
  componentDidMount() {
    this.setupFacebookPlayer();
  }

  shouldComponentUpdate = nextProps =>
    some(
      ['resource', 'credit', 'description'],
      value => nextProps.content[value] !== this.props.content[value]
    );

  componentDidUpdate = prevProps => {
    if (prevProps.content.resource !== this.props.content.resource) {
      // Remove the previously instantiated player
      while (this.containerDomNode.firstChild) {
        this.containerDomNode.removeChild(this.containerDomNode.firstChild);
      }
      this.setupFacebookPlayer();
    }
  };

  setupFacebookPlayer() {
    const videoUrl = this.videoUrl();
    if (!videoUrl) {
      return;
    }

    /*
     * This is intentionally not a JSX block in render(), because it will
     * be modified by the external API code. And on update, we have to
     * to replace it with a new one.
     */
    const playerDiv = `
      <div class="fb-video"
        id="${this.playerId()}"
        data-href="${videoUrl}"></div>
    `;
    this.containerDomNode.innerHTML = playerDiv;

    initializeFacebookPlayer(
      this.playerId(), this.containerDomNode, this.customEvents()
    );
  }

  videoUrl = () => this.props.content.facebook_video_id
  // The section is an Internal Video Section with an attached FB video id
    ? `https://www.facebook.com/video.php?v=${this.props.content.facebook_video_id}`
  // else, the section is a regular external video section with a FB embed
    : this.videoUrlForNativeFacebookSection();

  videoId = () =>
    this.props.content.facebook_video_id
  || this.videoUrlForNativeFacebookSection().match(/(\d+)\/?$/)[1];

  videoUrlForNativeFacebookSection = memoize(
    () => extractVideoUrlFromEmbedCode(this.props.content.resource),
    () => this.props.content.resource
  );

  customEvents = () => ({
    ready: this.props.readyHandler,
    startedPlaying: () => {
      sendAnalyticsEvent('External Video', 'Play', 'Facebook');
      this.props.clickHandler();
    },
    paused: () => {
      sendAnalyticsEvent('External Video', 'Paused', 'Facebook');
    },
    finishedPlaying: () => {
      sendAnalyticsEvent('External Video', 'Complete', 'Facebook');
      this.props.completionHandler();
    }
  })

  contentCaption() {
    if (this.props.shouldHideContentCaption) {
      return null;
    }
    const { credit, description } = this.props.content;
    return <ContentCaption credit={credit} description={description} />;
  }

  /*
   * Important for the playerId to be as unique as possible, so that the
   * Facebook API doesn't get confused. We can use the section index
   * and the numerical part of the video URL as keys.
   */
  playerId() {
    const fbId = this.videoId();
    if (!this.props.sectionIndex) { // Episode page
      return `fb-video-episode-${fbId}`;
    }
    return `fb-video-${this.props.sectionIndex}-${fbId}`;
  }

  setContainerDomNodeRef = element => {
    this.containerDomNode = element;
  }

  render() {
    const aspectRatio = findAspectRatioFromResource(this.props.content.resource);

    return (
      <div className="section-external-video facebook">
        <div
          className={`video-container ratio-${aspectRatio}`}
          ref={this.setContainerDomNodeRef}
        />
        {this.contentCaption()}
      </div>
    );
  }
}

export default withEscapeHatch({
  timeout: FB_READY_HANDLER_TIMEOUT
})(FacebookVideoSection);

FacebookVideoSection.propTypes = {
  content: PropTypes.shape({
    resource: PropTypes.string.isRequired,
    credit: PropTypes.string,
    description: PropTypes.string,
    facebook_video_id: PropTypes.string
  }),
  readyHandler: PropTypes.func,
  timeoutHandler: PropTypes.func,
  clickHandler: PropTypes.func,
  completionHandler: PropTypes.func,
  shouldHideContentCaption: PropTypes.bool,
  sectionIndex: PropTypes.number
};
